import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { executionModeSchema, type ExecutionMode } from './workspace';
import { runStageNames, runStageNameSchema, type RunStageName } from './stage';

/**
 * How a workflow type's `Review` stage is satisfied (Epic L wires the runtime):
 * - `none`   informational work; auto-approve + log
 * - `ai`     independent AI / self review for non-destructive work
 * - `human`  a human reviewer must approve material outcomes
 */
export const reviewModes = ['none', 'ai', 'human'] as const;
export const reviewModeSchema = z.enum(reviewModes);
export type ReviewMode = z.infer<typeof reviewModeSchema>;

/**
 * A first-class workflow type (Epic K). Declares — per workspace — the execution
 * mode, review behaviour, enabled pipeline stages, reviewer role labels, and the
 * confidence threshold that govern every run classified into this type.
 */
export const workflowTypeSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema,
    key: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[a-z0-9_.-]+$/, 'key must be kebab/snake case'),
    name: z.string().min(1).max(200),
    executionMode: executionModeSchema.default('assist'),
    reviewMode: reviewModeSchema.default('human'),
    /** Ordered subset of the 11 stages; empty = the default full flow. */
    enabledStages: z.array(runStageNameSchema).default([]),
    /** Role labels (not user ids) that may review outcomes of this type. */
    reviewerRoles: z.array(z.string()).default([]),
    confidenceThreshold: z.number().min(0).max(1).default(0.7),
  })
  .merge(timestampsSchema);
export type WorkflowType = z.infer<typeof workflowTypeSchema>;

export const createWorkflowTypeSchema = workflowTypeSchema.pick({
  key: true,
  name: true,
  executionMode: true,
  reviewMode: true,
  enabledStages: true,
  reviewerRoles: true,
  confidenceThreshold: true,
});
export type CreateWorkflowType = z.infer<typeof createWorkflowTypeSchema>;

export const updateWorkflowTypeSchema = createWorkflowTypeSchema.partial();
export type UpdateWorkflowType = z.infer<typeof updateWorkflowTypeSchema>;

/** Per-role override of the enabled stage set within a workflow type (Epic K2). */
export const agentStageConfigSchema = z
  .object({
    tenantId: idSchema,
    agentId: idSchema,
    workflowTypeKey: z.string().min(1).max(80),
    enabledStages: z.array(runStageNameSchema).default([]),
  })
  .merge(timestampsSchema.partial());
export type AgentStageConfig = z.infer<typeof agentStageConfigSchema>;

export const upsertAgentStageConfigSchema = z.object({
  agentId: idSchema,
  workflowTypeKey: z.string().min(1).max(80),
  enabledStages: z.array(runStageNameSchema),
});
export type UpsertAgentStageConfig = z.infer<typeof upsertAgentStageConfigSchema>;

/**
 * The resolved execution plan for a single run (Epic K3). Produced by
 * `resolveRunPlan` from a workflow type (+ optional per-agent override and a
 * workspace default), consumed by the runtime to drive which stages run and how
 * the review gate behaves.
 */
export interface RunPlan {
  enabledStages: RunStageName[];
  executionMode: ExecutionMode;
  reviewMode: ReviewMode;
  reviewerRoles: string[];
  confidenceThreshold: number;
}

/** Execute is the reasoning loop and Done is terminal — both always run. */
export const ALWAYS_ON_STAGES: RunStageName[] = ['Execute', 'Done'];

/** Order an arbitrary stage set by the canonical flow and guarantee Execute + Done. */
export function orderStages(stages: readonly RunStageName[]): RunStageName[] {
  const set = new Set<RunStageName>(stages);
  for (const s of ALWAYS_ON_STAGES) set.add(s);
  return runStageNames.filter((s) => set.has(s));
}

export const defaultRunPlan: RunPlan = {
  enabledStages: [...runStageNames],
  executionMode: 'assist',
  reviewMode: 'none',
  reviewerRoles: [],
  confidenceThreshold: 0.7,
};

export interface ResolveRunPlanInput {
  /** The matched workflow type, if any. */
  workflowType?: {
    executionMode: ExecutionMode;
    reviewMode: ReviewMode;
    enabledStages: RunStageName[];
    reviewerRoles: string[];
    confidenceThreshold: number;
  } | null;
  /** Per-agent stage override for this type, if configured. */
  agentOverrideStages?: RunStageName[] | null;
  /** Workspace fallback execution mode when no type matched. */
  workspaceDefaultMode?: ExecutionMode | null;
}

/**
 * Pure resolution of a run plan — shared by the API (preview) and the runtime
 * (execution) so both compute identical plans. An empty `enabledStages` means
 * "use the default full flow"; a per-agent override (when non-empty) wins.
 */
export function resolveRunPlan(input: ResolveRunPlanInput): RunPlan {
  const wt = input.workflowType ?? null;
  const baseStages =
    wt && wt.enabledStages.length > 0 ? wt.enabledStages : [...runStageNames];
  const stages =
    input.agentOverrideStages && input.agentOverrideStages.length > 0
      ? input.agentOverrideStages
      : baseStages;
  const executionMode = wt?.executionMode ?? input.workspaceDefaultMode ?? 'assist';
  // No workflow type → don't block on review; explicit types opt into ai/human.
  let reviewMode: ReviewMode = wt?.reviewMode ?? 'none';
  // Epic L / §5: `draft` always waits for an explicit approval, so it must be reviewed.
  if (executionMode === 'draft' && reviewMode === 'none') reviewMode = 'human';
  return {
    enabledStages: orderStages(stages),
    executionMode,
    reviewMode,
    reviewerRoles: wt?.reviewerRoles ?? [],
    confidenceThreshold: wt?.confidenceThreshold ?? 0.7,
  };
}
